import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { CustomSteleType } from 'src/components/3d/Stele';
import {
  Assembly,
  updateAssembly,
  updatePictureOfAssembly,
} from 'src/services/assemblies';
import { Buckets } from 'src/services/file';
import { orderQuotes, updateAssemblyQuote } from 'src/services/quotes';
import { handleMutation } from 'src/utils/api.utils';
import { getAssemblyObject, getQuoteLine } from 'src/utils/configurator.utils';
import { uploadImage } from 'src/utils/file.utils';
import * as THREE from 'three';
import { PieceDetail } from './useConfigurator';
import { QuoteStep } from 'src/Screens/project/components/NavBar';

/**
 * Extrait les IDs de stock uniques à partir d'une liste de PieceDetail.
 * Les stocks de type 'assembly' sont dédupliqués.
 */
export const getStocksIds = (elements: PieceDetail[]): string[] => {
  const stocks = elements.map((element) => {
    return { id: element.stockId, type: element.stockType };
  });

  const filteredStocks = stocks
    .reduce(
      (acc: { id: string | undefined; type: string | undefined }[], stock) => {
        if (stock.type === 'assembly') {
          const existingAssemblyStock = acc.find(
            (s) => s.type === 'assembly' && s.id === stock.id,
          );
          if (!existingAssemblyStock) {
            acc.push(stock);
          }
        } else {
          acc.push(stock);
        }
        return acc;
      },
      [],
    )
    .map((stock) => stock.id)
    .filter((id) => id !== undefined);

  return filteredStocks as string[];
};

/**
 * Hook qui gère la sauvegarde du monument et la soumission du devis.
 */
export const useConfiguratorSave = (
  pieces: any[],
  customSteles: CustomSteleType[],
  activePieces: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[],
  patterns: {
    [key: string]: {
      [key: string]: any;
    }[];
  },
  assemblyId: string | undefined,
  gl: THREE.WebGLRenderer | undefined,
  assembly: Assembly | null,
  onRefresh: () => void,
  getAssemblyDetails: () => Promise<{
    underAssemblies: {
      pieces: PieceDetail[];
      underAssemblyName: string;
      underAssemblyUUID: string;
      x: number;
      y: number;
      z: number;
      size: number;
    }[];
    pieces: PieceDetail[];
    steles: PieceDetail[];
    platings: PieceDetail[];
    soles: PieceDetail[];
    boxes: PieceDetail[];
    accesories: PieceDetail[];
    patterns: {
      ttcPrice: number;
      htPrice: number;
      name: string;
      description: string;
      quantity: number;
      extra: any[];
      leaderGranitEngraving: boolean;
    }[];
    engravings: any[];
  }>,
  takePictures: () => Promise<string[]>,
  step: QuoteStep,
  getValues: () => any,
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const updateMonument = useCallback(async () => {
    const assemblyObject = getAssemblyObject(
      pieces,
      customSteles,
      customPlatings,
      activePieces,
      customSoles,
      customBoxes,
      patterns,
    );

    try {
      await updateAssembly(
        assemblyObject,
        assemblyId === undefined ? '' : assemblyId,
      );
    } catch {
      toast.error('Erreur pendant la mise à jour du document');
    }
  }, [
    pieces,
    customSteles,
    customPlatings,
    customSoles,
    activePieces,
    customBoxes,
    patterns,
    assemblyId,
  ]);

  const handleSave = useCallback(async () => {
    if (assembly?.quote?.status === 'DRAFT') {
      setIsLoading(true);
      try {
        await updateMonument();
      } finally {
        setIsLoading(false);
      }
    }
  }, [updateMonument, assembly?.quote?.status]);

  const submit = useCallback(
    async ({
      detailed,
      discount,
      hasToBeOrdered,
      installations,
    }: {
      detailed?: boolean;
      discount?: number;
      hasToBeOrdered?: boolean;
      installations?: { price: number; name: string }[];
    }) => {
      setIsLoading(true);

      await updateMonument();
      gl?.domElement.toBlob(
        async (blob) => {
          if (blob) {
            const file = new File([blob], 'image.jpeg', { type: 'image/jpeg' });

            const pictureName = await uploadImage(
              file,
              Buckets.BUCKET_3D_PIECES,
            );

            if (pictureName && assembly?.id) {
              await updatePictureOfAssembly(assembly.id, pictureName);
            }
          }
        },
        'image/jpeg',
        0.5,
      );

      const assemblyDetails = await getAssemblyDetails();

      const stocksIds: string[] = getStocksIds([
        ...assemblyDetails.pieces,
        ...assemblyDetails.steles,
        ...assemblyDetails.platings,
        ...assemblyDetails.boxes,
        ...assemblyDetails.soles,
        ...assemblyDetails.accesories,
      ]);
      const quoteLines = getQuoteLine(assemblyDetails, discount);

      try {
        const [assemblyImage, ...images] = await takePictures();

        if (assemblyImage && assembly?.id) {
          await updatePictureOfAssembly(assembly.id, assemblyImage);
        }

        const imagesUrls = images.map(
          (image) => `https://3d-pieces.s3.eu-west-3.amazonaws.com/${image}`,
        );

        await handleMutation(
          (datas) =>
            updateAssemblyQuote({
              ...datas,
              step,
              status: 'QUOTE',
              oldAssemblyId: Number(assembly?.id),
              detailed,
              discount,
              ...getValues(),
              quoteLines,
              installations,
              quoteImages: imagesUrls,
              stocksIds,
            }),
          {},
          () => {
            onRefresh();
            toast.success(
              `Votre devis a bien été ${
                hasToBeOrdered ? 'commandé' : 'sauvegardé'
              }`,
            );
          },
        );

        if (hasToBeOrdered) await orderQuotes([Number(assembly?.id)]);
      } catch {
        toast.error('Erreur pendant la mise à jour du document');
      }
      setIsLoading(false);
    },
    [
      assembly?.id,
      onRefresh,
      step,
      gl?.domElement,
      getValues,
      getAssemblyDetails,
      takePictures,
      updateMonument,
    ],
  );

  return {
    isLoading,
    updateMonument,
    handleSave,
    submit,
  };
};
